import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApiMethod } from '../../types/types';
import {
  FormApiMethodsRequestData,
  HandlerRequestContext,
} from '../../api/types/types';
import { useDatasetFormData } from '../../hooks/context/useDatasetFormData';
import { useRequestDataContext } from '../../hooks/context/useRequestDataContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { useFieldHandlerEngine } from '../../hooks/useFieldHandlerEngine';
import { getHandlerApiMethods } from '../../utils/handlers/getHandlerApiMethods';
import { usePendingFullscreenHandler } from './hooks/usePendingFullscreenHandler';
import { usePendingFocusField } from './hooks/usePendingFocusField';
import { createRequestBody } from '../DatasetFormDataContext/utils/createRequestBody';
import { FormHandlersContextType } from '../../types/context';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';

const defaultValues: FormHandlersContextType = {
  isProvided: false,
  getFieldCallbacks: () => ({}),
  runHeaderHandler: async () => {},
};

export const FormHandlersContext =
  createContext<FormHandlersContextType>(defaultValues);

// Thin adapter over the shared field handler engine: supplies the dataset-form
// data access (form definition, request data scope, patchFormData) and keeps
// the form-only extras (header handlers, pending fullscreen replay).
const FormHandlersProvider = ({ children }: { children: ReactNode }) => {
  const { form, patchFormData, initialValues } = useDatasetFormData();
  const requestData = useRequestDataContext();
  const handleApiError = useApiErrorHandling();
  const { getResponseActionHandler } = useResponseActionsContext();

  // Populated once when the form definition arrives; handlers come with
  // form.apiMethods and the form is fetched a single time per page load.
  const [handlerApiMethods, setHandlerApiMethods] = useState<
    Record<string, ApiMethod>
  >(getHandlerApiMethods(form?.apiMethods));

  // The options close over the current render values; the engine reads them
  // through an internal ref, so its callbacks stay stable-identity.
  const engine = useFieldHandlerEngine({
    getApiMethod: (method) => handlerApiMethods[method],
    getItems: () => form?.items ?? [],
    buildContext: (formPatch, focusedItem) => {
      const base = requestData as FormApiMethodsRequestData | undefined;

      if (!base) {
        return undefined;
      }

      // formPatch carries the just-changed field value so the handler sees it
      // even before React commits the setState.
      const rawMergedForm = formPatch
        ? { ...(base.form ?? {}), ...formPatch }
        : (base.form ?? {});
      // Apply the same sanitization submit uses — drops static fields, applies
      // omitIfEmpty, converts '' → null for non-string types — so handlers see
      // the exact shape that would be sent on form submit. initialValues keep
      // keys without an item definition in the payload, same as on submit.
      const mergedForm = form
        ? createRequestBody(form, rawMergedForm, initialValues ?? undefined)
        : rawMergedForm;

      // Cast: FormApiMethodsRequestData is JSON-shaped, TS just can't prove it.
      return {
        ...base,
        form: mergedForm,
        focusedItem,
      } as unknown as HandlerRequestContext;
    },
    // Wrapped so the engine's fire-time context (2nd argument) doesn't leak
    // into patchFormData — forms patch a single form, no capture needed.
    applyFieldUpdates: (updates) => patchFormData(updates),
    getResponseActionHandler,
    handleApiError,
  });

  const runHeaderHandler = useCallback<
    FormHandlersContextType['runHeaderHandler']
  >(
    async (method, triggerData) => {
      // Header buttons must always fire — clear last formPatch entry so the
      // value-aware dedup in runMethod can't suppress a repeated click.
      engine.clearMethodDedup(method);
      await engine.runMethod(method, triggerData);
    },
    [engine]
  );

  const value = useMemo<FormHandlersContextType>(
    () => ({
      isProvided: true,
      getFieldCallbacks: engine.getFieldCallbacks,
      runHeaderHandler,
    }),
    [engine, runHeaderHandler]
  );

  usePendingFullscreenHandler(engine.fireEvent);
  usePendingFocusField();

  useEffect(() => {
    setHandlerApiMethods(getHandlerApiMethods(form?.apiMethods));
  }, [form?.apiMethods]);

  return (
    <FormHandlersContext.Provider value={value}>
      {children}
    </FormHandlersContext.Provider>
  );
};

export default FormHandlersProvider;
